/**
 * Generic Page Visit Tracker
 * Tracks when users visit specific pages using configurable sessionStorage/localStorage flags
 */

class PageVisitTracker {
  constructor() {
    // Get configuration from theme settings (passed via theme.liquid)
    this.config = window.pageVisitConfig || {};
    this.enabled = this.config.enabled || false;
    this.trackedUrls = this.config.trackedUrls || [];
    this.flagName = this.config.flagName || 'special_page_visited';
    this.storageKeyPrefix = this.config.storageKeyPrefix || 'page_visit';
    this.storageType = this.config.storageType || 'session';
    
    // Get current URL info
    this.currentUrl = this.getCurrentUrl();
    this.currentPath = this.getCurrentPath();
    
    // Debug output
    console.log('[PageVisitTracker] Config:', {
      enabled: this.enabled,
      trackedUrls: this.trackedUrls,
      flagName: this.flagName,
      currentPath: this.currentPath,
      currentUrl: this.currentUrl
    });
    
    // Initialize if enabled
    if (this.enabled) {
      this.init();
    } else {
      console.log('[PageVisitTracker] Tracking is disabled');
    }
  }

  /**
   * Initialize the tracker
   */
  init() {
    // Track visit if on any of the configured pages/URLs
    if (this.isTrackedUrl()) {
      this.setVisited();
      this.dispatchVisitEvent();
    }
    
    // Expose global methods for other scripts
    window.PageVisitTracker = {
      hasVisited: () => this.hasVisited(),
      getFlag: () => this.getFlag(),
      clearFlag: () => this.clearFlag(),
      isTrackedUrl: () => this.isTrackedUrl(),
      getTrackedUrls: () => this.trackedUrls,
      getCurrentUrl: () => this.currentUrl,
      getCurrentPath: () => this.currentPath
    };
  }

  /**
   * Get current full URL
   */
  getCurrentUrl() {
    return window.location.href;
  }

  /**
   * Get current path (without domain, query params, or hash)
   */
  getCurrentPath() {
    return window.location.pathname;
  }

  /**
   * Check if current URL matches any of the tracked URLs
   */
  isTrackedUrl() {
    if (!this.trackedUrls || this.trackedUrls.length === 0) {
      console.log('[PageVisitTracker] No tracked URLs configured');
      return false;
    }

    const currentPath = this.currentPath;
    const currentUrl = this.currentUrl;

    console.log('[PageVisitTracker] Checking URL match:', {
      currentPath,
      currentUrl,
      trackedUrls: this.trackedUrls
    });

    const isMatch = this.trackedUrls.some(trackedUrl => {
      // Normalize URLs for comparison
      const normalizedTracked = this.normalizeUrl(trackedUrl);
      const normalizedCurrent = this.normalizeUrl(currentUrl);
      const normalizedCurrentPath = this.normalizeUrl(currentPath);

      console.log('[PageVisitTracker] Comparing:', {
        trackedUrl,
        normalizedTracked,
        normalizedCurrentPath,
        normalizedCurrent
      });

      // Check exact matches
      if (normalizedTracked === normalizedCurrent || 
          normalizedTracked === normalizedCurrentPath) {
        console.log('[PageVisitTracker] ✅ Exact match found!');
        return true;
      }

      // Check if tracked URL is a path and matches current path
      if (trackedUrl.startsWith('/') && trackedUrl === currentPath) {
        console.log('[PageVisitTracker] ✅ Path match found!');
        return true;
      }

      // Check if current path ends with tracked path (for relative URLs)
      if (currentPath.endsWith(normalizedTracked)) {
        console.log('[PageVisitTracker] ✅ Ends-with match found!');
        return true;
      }

      return false;
    });

    console.log('[PageVisitTracker] URL match result:', isMatch);
    return isMatch;
  }

  /**
   * Normalize URL for comparison (remove trailing slashes, query params, etc.)
   */
  normalizeUrl(url) {
    if (!url) return '';
    
    // Remove query parameters and hash
    let normalized = url.split('?')[0].split('#')[0];
    
    // Remove trailing slash (except for root)
    if (normalized.length > 1 && normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
    }
    
    return normalized.toLowerCase();
  }

  /**
   * Get storage object based on configuration
   */
  getStorage() {
    return this.storageType === 'local' ? localStorage : sessionStorage;
  }

  /**
   * Get full storage key
   */
  getStorageKey() {
    return `${this.storageKeyPrefix}_${this.flagName}`;
  }

  /**
   * Set the visited flag
   */
  setVisited() {
    const storage = this.getStorage();
    const key = this.getStorageKey();
    storage.setItem(key, 'true');
    
    // Console output when visit is tracked
    console.log(`[PageVisitTracker] Visit tracked! Flag "${this.flagName}" set for URL: ${this.currentPath}`);
  }

  /**
   * Check if user has visited the tracked page
   */
  hasVisited() {
    const storage = this.getStorage();
    const key = this.getStorageKey();
    return storage.getItem(key) === 'true';
  }

  /**
   * Get the flag value (alias for hasVisited for consistency)
   */
  getFlag() {
    return this.hasVisited();
  }

  /**
   * Clear the visited flag
   */
  clearFlag() {
    const storage = this.getStorage();
    const key = this.getStorageKey();
    storage.removeItem(key);
    
    // Debug log (only in development)
    if (window.Shopify && window.Shopify.designMode) {
      console.log(`[PageVisitTracker] Flag cleared: ${key}`);
    }
  }

  /**
   * Dispatch custom event when page is visited
   */
  dispatchVisitEvent() {
    const event = new CustomEvent('pageVisitTracked', {
      detail: {
        flagName: this.flagName,
        currentUrl: this.currentUrl,
        currentPath: this.currentPath,
        trackedUrls: this.trackedUrls,
        storageKey: this.getStorageKey()
      }
    });
    
    document.dispatchEvent(event);
    
    // Also dispatch namespaced event for specific flag
    const specificEvent = new CustomEvent(`pageVisit:${this.flagName}`, {
      detail: {
        currentUrl: this.currentUrl,
        currentPath: this.currentPath,
        trackedUrls: this.trackedUrls,
        storageKey: this.getStorageKey()
      }
    });
    
    document.dispatchEvent(specificEvent);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PageVisitTracker();
  });
} else {
  new PageVisitTracker();
}

// Export for module systems (if needed)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PageVisitTracker;
}



